import { Location } from '@angular/common';
import { Injectable, TemplateRef } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ROUTES } from '../../components/sidebar/sidebar.component';

export interface PageHeaderState {
  /** Main page title shown in the global header. */
  title: string;
  /** Optional muted line below the title (former card-category text). */
  subtitle?: string;
  /** Optional numeric counter (e.g. list length). */
  count?: number | null;
  /** Label rendered next to the counter (e.g. "plantillas"). */
  countLabel?: string;
  /** Free-form suffix appended after the counter (e.g. "· 3 seleccionadas"). */
  countSuffix?: string;
  /**
   * Back-arrow behaviour:
   *  - a string  → router link to navigate to
   *  - true      → use Location.back()
   *  - false/undefined → auto: show when the current route is not a
   *    top-level sidebar entry
   */
  backTo?: string | boolean;
}

const EMPTY_STATE: PageHeaderState = { title: '' };

@Injectable({ providedIn: 'root' })
export class PageHeaderService {
  private readonly stateSubject = new BehaviorSubject<PageHeaderState>(
    EMPTY_STATE,
  );
  private readonly actionsSubject =
    new BehaviorSubject<TemplateRef<unknown> | null>(null);

  /** Current header state stream (title, counter, back arrow). */
  readonly state$: Observable<PageHeaderState> =
    this.stateSubject.asObservable();

  /** Template projected into the header's action slot. */
  readonly actions$: Observable<TemplateRef<unknown> | null> =
    this.actionsSubject.asObservable();

  private readonly topLevelPaths = ROUTES.map((route) => route.path);

  constructor(
    private router: Router,
    private location: Location,
  ) {
    // Clear stale state on every navigation so a page that forgets to set
    // its header never inherits the previous one.
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.stateSubject.next(EMPTY_STATE);
        this.actionsSubject.next(null);
      });
  }

  /** Replace the whole header state. */
  set(state: PageHeaderState): void {
    this.stateSubject.next({ ...EMPTY_STATE, ...state });
  }

  /** Merge a partial update into the current header state. */
  patch(partial: Partial<PageHeaderState>): void {
    this.stateSubject.next({ ...this.stateSubject.value, ...partial });
  }

  /**
   * Register (or clear) the template rendered in the action slot.
   * Deferred to a microtask so callers can invoke it from ngAfterViewInit
   * without triggering ExpressionChangedAfterItHasBeenCheckedError in the
   * already-rendered navbar.
   */
  setActions(template: TemplateRef<unknown> | null): void {
    Promise.resolve().then(() => this.actionsSubject.next(template));
  }

  /** Reset everything — call from a component's ngOnDestroy. */
  clear(): void {
    this.stateSubject.next(EMPTY_STATE);
    this.actionsSubject.next(null);
  }

  /**
   * Whether a back arrow should be shown for the current state.
   * Explicit `backTo` wins; otherwise fall back to "not a menu route".
   */
  shouldShowBack(state: PageHeaderState): boolean {
    if (state.backTo === true || typeof state.backTo === 'string') {
      return true;
    }
    if (state.backTo === false) {
      return false;
    }
    return !this.isTopLevelRoute();
  }

  /** Perform the back navigation for the given state. */
  goBack(state: PageHeaderState): void {
    if (typeof state.backTo === 'string') {
      this.router.navigateByUrl(state.backTo);
      return;
    }
    this.location.back();
  }

  private isTopLevelRoute(): boolean {
    const url = this.router.url.split('?')[0].split('#')[0];
    return this.topLevelPaths.some((path) => url === path);
  }
}
